import React, { useState, useEffect } from 'react';
import {
  User,
  Bell,
  Shield,
  Globe,
  Palette,
  Database,
  Save,
  Eye,
  EyeOff,
  Upload,
  Key,
} from 'lucide-react';
import { authApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';

interface SettingsSection {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
}

const settingsSections: SettingsSection[] = [
  { id: 'profile', title: 'Perfil', icon: User },
  { id: 'notifications', title: 'Notificaciones', icon: Bell },
  { id: 'security', title: 'Seguridad', icon: Shield },
  { id: 'platform', title: 'Plataforma', icon: Globe },
  { id: 'appearance', title: 'Apariencia', icon: Palette },
  { id: 'integrations', title: 'Integraciones', icon: Database },
];

export const SettingsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const { user, updateUser } = useAuthStore();

  // Profile settings state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bio: '',
    avatar: '',
  });

  // Notification settings state
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: true,
    newStudents: true,
    newPayments: true,
    systemAlerts: true,
    weeklyReports: false,
  });

  // Security settings state
  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
  });

  // 2FA Modal states - copied from students platform
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrData, setQrData] = useState<{qrCode: string, secret: string, manualEntryKey: string} | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [user2FAEnabled, setUser2FAEnabled] = useState(false);

  // Platform settings state
  const [platform, setPlatform] = useState({
    siteName: 'Escuela Agentesia',
    siteDescription: 'Plataforma de aprendizaje online',
    defaultLanguage: 'es',
    timezone: 'America/Bogota',
    currency: 'COP',
    allowRegistrations: true,
    requireEmailVerification: true,
  });

  // Load profile data on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const response = await authApi.getProfile();
        const profileData = response.data.user || response.data;
        
        setProfileData({
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          bio: profileData.bio || '',
          avatar: profileData.avatar || '',
        });

        // Cargar estado de 2FA
        setUser2FAEnabled(profileData.twoFactorEnabled || false);
      } catch (error) {
        console.error('Error loading profile:', error);
        // Use existing user data from store if API fails
        if (user) {
          setProfileData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phone: '',
            bio: '',
            avatar: user.avatar || '',
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif)/)) {
      toast.error('Por favor selecciona una imagen válida (JPG, PNG, GIF)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setIsUploadingAvatar(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('https://api.escuela.agentesia.cloud/api/v1/auth/profile/avatar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Error al subir la imagen');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const avatarUrl = data.data.url || data.data.avatarUrl;
        const updatedUser = data.data.user;
        
        if (avatarUrl) {
          setProfileData(prev => ({ ...prev, avatar: avatarUrl }));
          
          // Update user in store directly with the response from upload
          if (updatedUser) {
            updateUser(updatedUser);
          } else {
            // Fallback: update just the avatar
            updateUser({ avatar: avatarUrl });
          }
          
          toast.success('Avatar actualizado exitosamente');
        }
      }
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast.error(error.message || 'Error al subir la imagen');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!confirm('¿Estás seguro de que quieres eliminar tu foto de perfil?')) {
      return;
    }

    try {
      setProfileData(prev => ({ ...prev, avatar: '' }));
      
      const response = await authApi.updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        avatar: ''
      });
      
      if (response.data) {
        updateUser(response.data.user || response.data);
      }
      
      toast.success('Foto de perfil eliminada exitosamente');
    } catch (error: any) {
      console.error('Error removing avatar:', error);
      toast.error('Error al eliminar la foto de perfil');
    }
  };

  // 2FA Functions - COPIADO EXACTAMENTE DE ESTUDIANTES
  const handleEnable2FA = async () => {
    try {
      const response = await authApi.enable2FA();
      
      if (response.data.success) {
        setQrData({
          qrCode: response.data.data.qrCode,
          secret: response.data.data.secret,
          manualEntryKey: response.data.data.manualEntryKey
        });
        setShow2FASetup(true);
        setShow2FAModal(false);
      }
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      toast.error('Error al generar código QR. Intenta nuevamente.');
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Ingresa un código de 6 dígitos');
      return;
    }

    try {
      const response = await authApi.verify2FA({ token: verificationCode });
      
      if (response.data.success) {
        setBackupCodes(response.data.data.backupCodes);
        setUser2FAEnabled(true);
        setShow2FASetup(false);
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast.error('Código inválido. Intenta nuevamente.');
    }
  };

  const handleDisable2FA = async () => {
    const password = security.currentPassword;
    if (!password) {
      toast.error('Ingresa tu contraseña para desactivar 2FA');
      return;
    }

    try {
      const response = await authApi.disable2FA({ password });
      
      if (response.data.success) {
        setUser2FAEnabled(false);
        setSecurity({ ...security, currentPassword: '' });
        toast.success('2FA desactivado exitosamente.');
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error('Error al desactivar 2FA. Verifica tu contraseña.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      if (activeSection === 'profile') {
        // Save profile data
        const response = await authApi.updateProfile({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          avatar: profileData.avatar
        });
        
        // Update user in store
        if (response.data) {
          updateUser(response.data.user || response.data);
        }
        toast.success('Perfil actualizado exitosamente');
        
      } else if (activeSection === 'security') {
        // Change password
        if (security.newPassword && security.currentPassword) {
          if (security.newPassword !== security.confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
          }
          
          await authApi.changePassword({
            currentPassword: security.currentPassword,
            newPassword: security.newPassword
          });
          
          // Clear password fields
          setSecurity(prev => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }));
          
          toast.success('Contraseña cambiada exitosamente');
        }
      } else {
        // Other sections - just show success (platform settings would need backend implementation)
        toast.success('Configuración guardada exitosamente');
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      const message = error?.response?.data?.message || 'Error al guardar la configuración';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const renderProfileSettings = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }
    
    return (
    <div className="space-y-6">
      <div className="flex items-center space-x-6">
        <div className="relative">
          <img
            src={profileData.avatar ? 
              (profileData.avatar.startsWith('http') ? profileData.avatar : `https://api.escuela.agentesia.cloud${profileData.avatar}`) : 
              `https://api.dicebear.com/7.x/avataaars/svg?seed=${profileData.email}`
            }
            alt="Avatar"
            className="w-24 h-24 rounded-full border-4 border-gray-200 object-cover"
          />
          <label className="absolute bottom-0 right-0 p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors cursor-pointer">
            {isUploadingAvatar ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Upload className="w-4 h-4" />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
              disabled={isUploadingAvatar}
            />
          </label>
        </div>
        
        <div>
          <h3 className="text-lg font-medium text-gray-900">Foto de Perfil</h3>
          <p className="text-sm text-gray-600">Sube una imagen de perfil (JPG, PNG, GIF, max 5MB)</p>
          {profileData.avatar && (
            <button 
              onClick={handleRemoveAvatar}
              className="mt-2 text-sm text-red-600 hover:text-red-700"
            >
              Eliminar foto
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nombre
          </label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Apellido
          </label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Teléfono
          </label>
          <input
            type="tel"
            value={profileData.phone}
            onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Biografía
        </label>
        <textarea
          value={profileData.bio}
          onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
    </div>
    );
  };

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Preferencias de Notificación</h3>
        
        <div className="space-y-4">
          {[
            { key: 'emailNotifications', label: 'Notificaciones por email', description: 'Recibe notificaciones importantes por correo' },
            { key: 'pushNotifications', label: 'Notificaciones push', description: 'Notificaciones en tiempo real en el navegador' },
            { key: 'newStudents', label: 'Nuevos estudiantes', description: 'Notificar cuando se registren nuevos estudiantes' },
            { key: 'newPayments', label: 'Nuevos pagos', description: 'Alertas sobre pagos y transacciones' },
            { key: 'systemAlerts', label: 'Alertas del sistema', description: 'Notificaciones sobre el estado del sistema' },
            { key: 'weeklyReports', label: 'Reportes semanales', description: 'Resumen semanal de actividad' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0">
              <div>
                <h4 className="text-sm font-medium text-gray-900">{item.label}</h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifications[item.key as keyof typeof notifications]}
                  onChange={(e) => setNotifications(prev => ({ 
                    ...prev, 
                    [item.key]: e.target.checked 
                  }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Cambiar Contraseña</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña Actual
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={security.currentPassword}
                onChange={(e) => setSecurity(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5 text-gray-400" />
                ) : (
                  <Eye className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva Contraseña
            </label>
            <input
              type="password"
              value={security.newPassword}
              onChange={(e) => setSecurity(prev => ({ ...prev, newPassword: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar Nueva Contraseña
            </label>
            <input
              type="password"
              value={security.confirmPassword}
              onChange={(e) => setSecurity(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Key size={20} className={user2FAEnabled ? "text-green-600" : "text-orange-600"} />
            <div>
              <h4 className="text-sm font-medium text-gray-900">Autenticación de Dos Factores</h4>
              <p className="text-sm text-gray-600">{user2FAEnabled ? 'Activada - Tu cuenta está protegida' : 'Añade una capa extra de seguridad a tu cuenta'}</p>
            </div>
          </div>
          {!user2FAEnabled ? (
            <button
              onClick={() => setShow2FAModal(true)}
              className="text-sm px-4 py-2 rounded transition-colors bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300"
            >
              Activar
            </button>
          ) : (
            <div className="flex items-end gap-3">
              <div className="flex flex-col min-w-0 flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  Contraseña para desactivar
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Tu contraseña actual"
                    value={security.currentPassword}
                    onChange={(e) => setSecurity({ ...security, currentPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button
                onClick={handleDisable2FA}
                disabled={!security.currentPassword}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Desactivar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPlatformSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nombre del Sitio
          </label>
          <input
            type="text"
            value={platform.siteName}
            onChange={(e) => setPlatform(prev => ({ ...prev, siteName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Idioma por Defecto
          </label>
          <select
            value={platform.defaultLanguage}
            onChange={(e) => setPlatform(prev => ({ ...prev, defaultLanguage: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="es">Español</option>
            <option value="en">English</option>
            <option value="fr">Français</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zona Horaria
          </label>
          <select
            value={platform.timezone}
            onChange={(e) => setPlatform(prev => ({ ...prev, timezone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="America/Bogota">Bogotá, Colombia</option>
            <option value="America/Mexico_City">Ciudad de México</option>
            <option value="America/New_York">Nueva York</option>
            <option value="Europe/Madrid">Madrid</option>
            <option value="UTC">UTC</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Moneda
          </label>
          <select
            value={platform.currency}
            onChange={(e) => setPlatform(prev => ({ ...prev, currency: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="COP">COP - Peso Colombiano</option>
            <option value="USD">USD - Dólar Americano</option>
            <option value="EUR">EUR - Euro</option>
            <option value="MXN">MXN - Peso Mexicano</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Descripción del Sitio
        </label>
        <textarea
          value={platform.siteDescription}
          onChange={(e) => setPlatform(prev => ({ ...prev, siteDescription: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Permitir Registros</h4>
            <p className="text-sm text-gray-600">Permitir que nuevos usuarios se registren</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={platform.allowRegistrations}
              onChange={(e) => setPlatform(prev => ({ ...prev, allowRegistrations: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Verificación de Email</h4>
            <p className="text-sm text-gray-600">Requerir verificación de email para nuevos usuarios</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={platform.requireEmailVerification}
              onChange={(e) => setPlatform(prev => ({ ...prev, requireEmailVerification: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'security':
        return renderSecuritySettings();
      case 'platform':
        return renderPlatformSettings();
      case 'appearance':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Apariencia</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tema de la Interfaz
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="light">Claro</option>
                    <option value="dark">Oscuro</option>
                    <option value="auto">Automático</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Principal
                  </label>
                  <div className="flex space-x-2">
                    {['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6B7280'].map((color) => (
                      <button
                        key={color}
                        className="w-8 h-8 rounded-full border-2 border-gray-200 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tamaño de Fuente
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="small">Pequeña</option>
                    <option value="medium">Mediana</option>
                    <option value="large">Grande</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Densidad de la Interfaz
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="compact">Compacta</option>
                    <option value="comfortable">Cómoda</option>
                    <option value="spacious">Espaciosa</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );
      case 'integrations':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Integraciones</h3>
              
              <div className="space-y-4">
                {/* Email Service */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <Database className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Servicio de Email</h4>
                        <p className="text-sm text-gray-600">SMTP para envío de notificaciones</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full mr-3">Conectado</span>
                      <button className="text-sm text-primary-600 hover:text-primary-700">Configurar</button>
                    </div>
                  </div>
                </div>

                {/* Payment Gateway */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <Database className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Pasarela de Pagos</h4>
                        <p className="text-sm text-gray-600">WOMPI para procesamiento de pagos</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full mr-3">Conectado</span>
                      <button className="text-sm text-primary-600 hover:text-primary-700">Configurar</button>
                    </div>
                  </div>
                </div>

                {/* Cloud Storage */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                        <Database className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Almacenamiento en la Nube</h4>
                        <p className="text-sm text-gray-600">Para archivos y contenido multimedia</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full mr-3">Configurar</span>
                      <button className="text-sm text-primary-600 hover:text-primary-700">Configurar</button>
                    </div>
                  </div>
                </div>

                {/* Analytics */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                        <Database className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Google Analytics</h4>
                        <p className="text-sm text-gray-600">Seguimiento de usuarios y comportamiento</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full mr-3">Desconectado</span>
                      <button className="text-sm text-primary-600 hover:text-primary-700">Conectar</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Configuración</h1>
        <p className="text-gray-600">
          Administra la configuración de tu perfil y plataforma
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Settings Navigation */}
        <div className="lg:w-64 space-y-1">
          {settingsSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                activeSection === section.id
                  ? 'bg-primary-100 text-primary-700 border border-primary-200'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <section.icon className="w-5 h-5" />
              <span className="font-medium">{section.title}</span>
            </button>
          ))}
        </div>

        {/* Settings Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {settingsSections.find(s => s.id === activeSection)?.title}
                </h2>
                
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>{isSaving ? 'Guardando...' : 'Guardar Cambios'}</span>
                </button>
              </div>

              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* 2FA Modal - copied from students platform */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg border border-gray-300 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Activar 2FA</h3>
              <button 
                onClick={() => setShow2FAModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="text-center">
                <Shield size={48} className="text-green-600 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">
                  Activar Autenticación en Dos Pasos
                </p>
                <p className="text-gray-600 text-sm mb-4">
                  Agrega una capa adicional de seguridad a tu cuenta usando una aplicación authenticator como Google Authenticator o Authy.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleEnable2FA}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 justify-center"
                >
                  <Shield size={16} />
                  Continuar
                </button>
                <button
                  onClick={() => setShow2FAModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Setup 2FA con QR - COPIADO EXACTAMENTE DE ESTUDIANTES */}
      {show2FASetup && qrData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Configurar Authenticator</h3>
              <button 
                onClick={() => setShow2FASetup(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-white font-medium mb-4">
                  Paso 1: Escanea este código QR con tu app authenticator:
                </p>
                <div className="bg-white p-4 rounded-lg inline-block">
                  <img src={qrData.qrCode} alt="QR Code" className="w-48 h-48" />
                </div>
              </div>
              
              <div className="bg-gray-800/50 p-4 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">
                  O ingresa manualmente esta clave:
                </p>
                <code className="text-cyan-400 text-sm font-mono break-all block">
                  {qrData.manualEntryKey}
                </code>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Paso 2: Ingresa el código de verificación (6 dígitos)
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleVerify2FA}
                  disabled={verificationCode.length !== 6}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Verificar y Activar
                </button>
                <button
                  onClick={() => setShow2FASetup(false)}
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Códigos de Respaldo */}
      {backupCodes.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg border border-gray-300 p-6 w-full max-w-md">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Shield size={32} className="text-green-600" />
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">¡2FA Activado!</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Guarda estos códigos de respaldo en un lugar seguro. Podrás usarlos si pierdes acceso a tu authenticator:
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg text-left max-h-40 overflow-y-auto border">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="font-mono text-sm text-blue-600 p-2 bg-white rounded text-center border">
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="text-xs text-orange-600 bg-orange-50 p-3 rounded border border-orange-200">
                ⚠️ Importante: Guarda estos códigos en un lugar seguro. No los volverás a ver.
              </div>
              
              <button
                onClick={() => {
                  setBackupCodes([]);
                  // 2FA ya está activado, no necesita alert
                }}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                He guardado los códigos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};